import { useState, useEffect, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Header } from '@/components/revenue-manager/Header';
import { StatsCards } from '@/components/revenue-manager/StatsCards';
import { MetricsRow } from '@/components/revenue-manager/MetricsRow';
import { TabNavigation } from '@/components/revenue-manager/TabNavigation';
import { PricingRulesTable } from '@/components/revenue-manager/PricingRulesTable';
import { AvailabilityRulesTable } from '@/components/revenue-manager/AvailabilityRulesTable';
import { EventsManager } from '@/components/revenue-manager/EventsManager';
import { CalendarView } from '@/components/revenue-manager/CalendarView';
import { AnalyticsView } from '@/components/revenue-manager/AnalyticsView';
import { CompetitorsView } from '@/components/revenue-manager/CompetitorsView';
import { AddPropertyDialog } from '@/components/revenue-manager/AddPropertyDialog';
import { CreatePricingRuleDialog } from '@/components/revenue-manager/CreatePricingRuleDialog';
import { CreateAvailabilityRuleDialog } from '@/components/revenue-manager/CreateAvailabilityRuleDialog';
import { CreateEventDialog } from '@/components/revenue-manager/CreateEventDialog';
import { ExportRulesDialog } from '@/components/revenue-manager/ExportRulesDialog';
import { ImportRulesDialog } from '@/components/revenue-manager/ImportRulesDialog';
import { useProperties, useCreateProperty, useUpdateProperty, useRevenueManagerBranches } from '@/hooks/use-properties';
import { usePricingRules, useCreatePricingRule, useUpdatePricingRule, useDeletePricingRule, useTogglePricingRule, useImportPricingRules } from '@/hooks/use-pricing-rules';
import { useAvailabilityRules, useCreateAvailabilityRule, useUpdateAvailabilityRule, useDeleteAvailabilityRule, useToggleAvailabilityRule, useImportAvailabilityRules } from '@/hooks/use-availability-rules';
import { useEvents, useCreateEvent, useUpdateEvent, useDeleteEvent, useImportEvents, useUpcomingEventsCount, useAllEvents } from '@/hooks/use-events';
import { useOTAChannels } from '@/hooks/use-ota-channels';
import { useRoomTypes } from '@/hooks/use-room-inventory';
import { useAuth } from '@/contexts/AuthContext';
import {
  Property,
  PricingRule,
  AvailabilityRule,
  Event,
  TabType,
} from '@/types/revenue-manager';
import { transformPricingRuleForImport, transformAvailabilityRuleForImport } from '@/utils/import-rule-transform';
import { ApiPricingRule } from '@/utils/pricing-rule-transform';
import { ApiAvailabilityRule } from '@/utils/availability-rule-transform';
import { executeRule } from '@/api/generic';
import { Loader2 } from 'lucide-react';

const Index = () => {
  const { toast } = useToast();
  const { businessDetails } = useAuth();

  // Fetch branches (real API)
  const { data: branchesData, isLoading: branchesLoading, error: branchesError } = useRevenueManagerBranches();
  const branches = useMemo(() => branchesData?.data || [], [branchesData]);

  // Debug logging
  // console.log('Branches data:', branchesData);
  // console.log('Branches:', branches);
  // console.log('Loading:', branchesLoading);
  // console.log('Error:', branchesError);

  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabType>('pricing');

  // Set default property when branches load
  useEffect(() => {
    if (branches.length > 0 && !selectedPropertyId) {
      const firstBranch = branches[0];
      const id = firstBranch.api_key && firstBranch.api_key.trim() !== ''
        ? firstBranch.cm_property_id
        : `${firstBranch.id}`;
      setSelectedPropertyId(id);
    }
  }, [branches, selectedPropertyId]);

  // Get selected branch ID for filtering room types
  useEffect(() => {
  }, [selectedPropertyId]);
  
  const selectedBranch = branches.find(b => {
    const branchId = b.api_key && b.api_key.trim() !== ''
      ? b.cm_property_id
      : `${b.id}`;
    return branchId === selectedPropertyId;
  });
  const selectedBranchId = selectedBranch?.id;
  const selectedOrgId = selectedBranch?.org_id;

  // Fetch room types to calculate base price
  const { data: roomTypesData } = useRoomTypes(selectedBranchId);

  // Transform branches to properties format with calculated base price
  const properties: Property[] = useMemo(() => {
    return branches.map(branch => {
      let basePrice = 1000; // Default

      // Calculate average base price for selected branch if room types data is available
      if (branch.id === selectedBranchId && roomTypesData && roomTypesData.length > 0) {
        const totalPrice = roomTypesData.reduce((sum, roomType) => {
          return sum + Number(roomType.basic_room_rate || 0);
        }, 0);
        basePrice = Math.round(totalPrice / roomTypesData.length);
      }

      // Use cm_property_id only if api_key exists, otherwise use branch.id as fallback
      const propertyId = branch.api_key && branch.api_key.trim() !== ''
        ? branch.cm_property_id
        : `${branch.id}`;

      return {
        id: propertyId,
        apiId: branch.api_key,
        name: branch.branch_name,
        location: `Branch ID: ${branch.id}`,
        basePrice,
        roomCount: 50
      };
    });
  }, [branches, selectedBranchId, roomTypesData]);

  // console.log('Properties:', properties);

  const selectedProperty = properties.find((p) => p.id === selectedPropertyId) || properties[0];
  const propertiesLoading = branchesLoading;
  // console.log("selectedProperty", selectedProperty)
  // Fetch property-specific data (use branch ID for API calls)
  const { data: pricingRulesData, isLoading: pricingLoading } = usePricingRules(String(selectedBranchId || ''));
  const { data: availabilityRulesData, isLoading: availabilityLoading } = useAvailabilityRules(String(selectedBranchId || ''));
  // const { data: eventsData, isLoading: eventsLoading } = useEvents(String(selectedOrgId || ''));
  const { data: eventsData, isLoading: eventsLoading } = useAllEvents();
  const { data: upcomingEventsCountData } = useUpcomingEventsCount();
  const { data: otaChannelsData } = useOTAChannels(selectedBranchId);
  const pricingRules = pricingRulesData?.data || [];
  const availabilityRules = availabilityRulesData?.data || [];
  const events = eventsData?.data || [];
  const upcomingEventsCount = upcomingEventsCountData?.count || 0;
  const otaChannels = otaChannelsData?.data || [];

  // Mutations (use branch ID to match query keys)
  const createPropertyMutation = useCreateProperty();
  const updatePropertyMutation = useUpdateProperty();
  const createPricingRuleMutation = useCreatePricingRule(String(selectedBranchId || ''));
  const updatePricingRuleMutation = useUpdatePricingRule(String(selectedBranchId || ''));
  const deletePricingRuleMutation = useDeletePricingRule(String(selectedBranchId || ''));
  const togglePricingRuleMutation = useTogglePricingRule(String(selectedBranchId || ''));
  const importPricingRulesMutation = useImportPricingRules();
  const createAvailabilityRuleMutation = useCreateAvailabilityRule(String(selectedBranchId || ''));
  const updateAvailabilityRuleMutation = useUpdateAvailabilityRule(String(selectedBranchId || ''));
  const deleteAvailabilityRuleMutation = useDeleteAvailabilityRule(String(selectedBranchId || ''));
  const toggleAvailabilityRuleMutation = useToggleAvailabilityRule(String(selectedBranchId || ''));
  const importAvailabilityRulesMutation = useImportAvailabilityRules();
  const createEventMutation = useCreateEvent();
  const updateEventMutation = useUpdateEvent();
  const deleteEventMutation = useDeleteEvent();
  const importEventsMutation = useImportEvents();

  // Dialog states
  const [addPropertyOpen, setAddPropertyOpen] = useState(false);
  const [createPricingRuleOpen, setCreatePricingRuleOpen] = useState(false);
  const [createAvailabilityRuleOpen, setCreateAvailabilityRuleOpen] = useState(false);
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [exportPricingOpen, setExportPricingOpen] = useState(false);
  const [importPricingOpen, setImportPricingOpen] = useState(false);
  const [exportAvailabilityOpen, setExportAvailabilityOpen] = useState(false);
  const [importAvailabilityOpen, setImportAvailabilityOpen] = useState(false);
  const [exportEventsOpen, setExportEventsOpen] = useState(false);
  const [importEventsOpen, setImportEventsOpen] = useState(false);
  const [editingPricingRule, setEditingPricingRule] = useState<PricingRule | null>(null);
  const [editingAvailabilityRule, setEditingAvailabilityRule] = useState<AvailabilityRule | null>(null);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);

  const handleAddProperty = (property: Omit<Property, 'id'>) => {
    createPropertyMutation.mutate(property, {
      onSuccess: (data) => {
        setSelectedPropertyId(data.data.id);
        toast({ title: 'Property added', description: `${property.name} has been added.` });
      },
    });
  };

  const handleBasePriceChange = (price: number) => {
    updatePropertyMutation.mutate({
      propertyId: selectedPropertyId,
      updates: { basePrice: price },
    }, {
      onSuccess: () => {
        toast({ title: 'Base price updated', description: `Base price updated to ${price}.` });
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update base price';
        toast({
          title: 'Error updating base price',
          description: errorMessage,
          variant: 'destructive'
        });
      },
    });
  };

  const handleSavePricingRule = (rule: ApiPricingRule) => {
    console.log('Saving pricing rule in API format:', rule);

    if (editingPricingRule && editingPricingRule.id) {
      updatePricingRuleMutation.mutate(
        { ruleId: editingPricingRule.id, updates: rule as any },
        {
          onSuccess: () => {
            toast({ title: 'Rule updated', description: `${rule.name} updated.` });
            setEditingPricingRule(null);
            setCreatePricingRuleOpen(false);
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update rule';
            toast({
              title: 'Error updating rule',
              description: errorMessage,
              variant: 'destructive'
            });
          },
        }
      );
    } else {
      createPricingRuleMutation.mutate(rule as any, {
        onSuccess: () => {
          toast({ title: 'Rule created', description: `${rule.name} created.` });
          setCreatePricingRuleOpen(false);
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create rule';
          toast({
            title: 'Error creating rule',
            description: errorMessage,
            variant: 'destructive'
          });
        },
      });
    }
  };

  const handleTogglePricingRule = (ruleId: string, isActive: boolean) => {
    togglePricingRuleMutation.mutate({ ruleId, isActive }, {
      onSuccess: () => {
        toast({ title: 'Rule toggled' });
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to toggle rule';
        toast({
          title: 'Error toggling rule',
          description: errorMessage,
          variant: 'destructive'
        });
      },
    });
  };

  const handleDeletePricingRule = (ruleId: string) => {
    deletePricingRuleMutation.mutate(ruleId, {
      onSuccess: () => {
        toast({ title: 'Rule deleted' });
      },
    });
  };

  const handleSaveAvailabilityRule = (rule: ApiAvailabilityRule) => {
    console.log('Saving availability rule in API format:', rule);

    if (editingAvailabilityRule && editingAvailabilityRule.id) {
      updateAvailabilityRuleMutation.mutate(
        { ruleId: editingAvailabilityRule.id, updates: rule as any },
        {
          onSuccess: () => {
            toast({ title: 'Rule updated', description: `${rule.name} updated.` });
            setEditingAvailabilityRule(null);
            setCreateAvailabilityRuleOpen(false);
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update rule';
            toast({
              title: 'Error updating rule',
              description: errorMessage,
              variant: 'destructive'
            });
          },
        }
      );
    } else {
      createAvailabilityRuleMutation.mutate(rule as any, {
        onSuccess: () => {
          toast({ title: 'Rule created', description: `${rule.name} created.` });
          setCreateAvailabilityRuleOpen(false);
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create rule';
          toast({
            title: 'Error creating rule',
            description: errorMessage,
            variant: 'destructive'
          });
        },
      });
    }
  };

  const handleToggleAvailabilityRule = (ruleId: string, currentStatus: boolean) => {
    toggleAvailabilityRuleMutation.mutate(
      { ruleId, isActive: !currentStatus },
      {
        onSuccess: () => {
          toast({ title: 'Rule toggled', description: 'Rule status updated.' });
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to toggle rule';
          toast({
            title: 'Error toggling rule',
            description: errorMessage,
            variant: 'destructive'
          });
        },
      }
    );
  };

  const handleDeleteAvailabilityRule = (ruleId: string) => {
    deleteAvailabilityRuleMutation.mutate(ruleId, {
      onSuccess: () => {
        toast({ title: 'Rule deleted' });
      },
    });
  };

  const handleSaveEvent = (event: Omit<Event, 'id'>) => {
    // Convert property IDs to branch IDs (numeric)
    // Property ID can be either cm_property_id or branch.id as string
    const branchIds = event.propertyIds
      .map(propId => {
        const branch = branches.find(b =>
          b.cm_property_id === propId || String(b.id) === propId
        );
        return branch ? String(branch.id) : null;
      })
      .filter((id): id is string => id !== null);

    const eventWithBranchIds = {
      ...event,
      propertyIds: branchIds
    };

    if (editingEvent) {
      updateEventMutation.mutate(
        { eventId: editingEvent.id, updates: eventWithBranchIds },
        {
          onSuccess: () => {
            toast({ title: 'Event updated', description: `${event.name} has been updated.` });
            setEditingEvent(null);
            setCreateEventOpen(false);
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update event';
            toast({
              title: 'Error updating event',
              description: errorMessage,
              variant: 'destructive'
            });
          },
        }
      );
    } else {
      createEventMutation.mutate(eventWithBranchIds, {
        onSuccess: () => {
          toast({ title: 'Event created', description: `${event.name} has been created.` });
          setCreateEventOpen(false);
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create event';
          toast({
            title: 'Error creating event',
            description: errorMessage,
            variant: 'destructive'
          });
        },
      });
    }
  };

  const handleDeleteEvent = (eventId: string) => {
    deleteEventMutation.mutate(eventId, {
      onSuccess: () => {
        toast({ title: 'Event deleted' });
      },
    });
  };

  const handleImportPricingRules = async (rules: any[], targetPropertyIds: string[]) => {
    // Convert alphanumeric property IDs to numeric branch IDs
    const branchIds = targetPropertyIds
      .map(propId => {
        // const branch = branches.find(b => b.cm_property_id === propId);
        const branch = branches.find(b =>
          b.cm_property_id === propId || String(b.id) === propId
        );
        return branch ? String(branch.id) : null;
      })
      .filter((id): id is string => id !== null);

    // Get user email for submitted_by field
    const submittedBy = (typeof businessDetails === 'object' && businessDetails?.email1)
      ? String(businessDetails.email1)
      : 'unknown@example.com';

    // Transform rules once - backend will handle multiple properties
    // Use first branch's orgId for transformation
    const firstBranch = branches.find(b => String(b.id) === branchIds[0]);
    const orgId = String(firstBranch?.org_id || selectedOrgId || '');
    const propertyId = branchIds[0] || '';

    const transformedRules = rules.map(rule =>
      transformPricingRuleForImport(rule, orgId, propertyId, submittedBy)
    );

    return new Promise<void>((resolve, reject) => {
      importPricingRulesMutation.mutate(
        { targetPropertyIds: branchIds, rules: transformedRules as any },
        {
          onSuccess: (response: any) => {
            const count = response?.data?.importedCount ?? response?.importedCount ?? transformedRules.length;
            toast({
              title: 'Rules imported',
              description: `${count} pricing rule${count !== 1 ? 's' : ''} imported successfully.`
            });
            resolve();
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to import pricing rules';
            toast({
              title: 'Import failed',
              description: errorMessage,
              variant: 'destructive'
            });
            reject(error);
          },
        }
      );
    });
  };

  const handleImportAvailabilityRules = async (rules: any[], targetPropertyIds: string[]) => {
    // Convert alphanumeric property IDs to numeric branch IDs
    const branchIds = targetPropertyIds
      .map(propId => {
        const branch = branches.find(b =>
          b.cm_property_id === propId || String(b.id) === propId
        );
        return branch ? String(branch.id) : null;
      })
      .filter((id): id is string => id !== null);

    // Get user email for submitted_by field
    const submittedBy = (typeof businessDetails === 'object' && businessDetails?.email1)
      ? String(businessDetails.email1)
      : 'unknown@example.com';

    // Transform rules once - backend will handle multiple properties
    // Use first branch's orgId for transformation
    const firstBranch = branches.find(b => String(b.id) === branchIds[0]);
    const orgId = String(firstBranch?.org_id || selectedOrgId || '');
    const propertyId = branchIds[0] || '';

    const transformedRules = rules.map(rule =>
      transformAvailabilityRuleForImport(rule, orgId, propertyId, submittedBy)
    );

    return new Promise<void>((resolve, reject) => {
      importAvailabilityRulesMutation.mutate(
        { targetPropertyIds: branchIds, rules: transformedRules as any },
        {
          onSuccess: (response: any) => {
            const count = response?.data?.importedCount ?? response?.importedCount ?? transformedRules.length;
            toast({
              title: 'Rules imported',
              description: `${count} availability rule${count !== 1 ? 's' : ''} imported successfully.`
            });
            resolve();
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to import availability rules';
            toast({
              title: 'Import failed',
              description: errorMessage,
              variant: 'destructive'
            });
            reject(error);
          },
        }
      );
    });
  };

  const handleImportEvents = async (eventsToImport: any[], targetPropertyIds: string[]) => {
    // Convert alphanumeric property IDs to numeric branch IDs
    const branchIds = targetPropertyIds
      .map(propId => {
        const branch = branches.find(b =>
          b.cm_property_id === propId || String(b.id) === propId
        );
        return branch ? String(branch.id) : null;
      })
      .filter((id): id is string => id !== null);

    return new Promise<void>((resolve, reject) => {
      importEventsMutation.mutate(
        { targetPropertyIds: branchIds, events: eventsToImport },
        {
          onSuccess: (response: any) => {
            const count = response?.data?.importedCount ?? response?.importedCount ?? eventsToImport.length;
            toast({
              title: 'Events imported',
              description: `${count} event${count !== 1 ? 's' : ''} imported successfully.`
            });
            resolve();
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to import events';
            toast({
              title: 'Import failed',
              description: errorMessage,
              variant: 'destructive'
            });
            reject(error);
          },
        }
      );
    });
  };

  const handleExecutePricingRule = async (ruleId: string) => {
    try {
      await executeRule(ruleId, 'PricingRule');
      toast({
        title: 'Rule executed',
        description: 'Pricing rule execution started successfully.'
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to execute rule';
      toast({
        title: 'Error executing rule',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const handleExecuteAvailabilityRule = async (ruleId: string) => {
    try {
      await executeRule(ruleId, 'PricingRule');
      toast({
        title: 'Rule executed',
        description: 'Availability rule execution started successfully.'
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to execute rule';
      toast({
        title: 'Error executing rule',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const renderTabContent = () => {
    if (!selectedProperty) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3 p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 !border-[#0f1729]"></div>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'pricing':
        return pricingLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3 p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 !border-[#0f1729]"></div>
            </div>
          </div>
        ) : (
          <PricingRulesTable
            rules={pricingRules}
            otaChannels={otaChannels}
            property={selectedProperty}
            basePrice={selectedProperty.basePrice}
            branchId={selectedBranchId}
            orgId={selectedOrgId}
            onCreateRule={() => { setEditingPricingRule(null); setCreatePricingRuleOpen(true); }}
            onEditRule={(rule) => { setEditingPricingRule(rule); setCreatePricingRuleOpen(true); }}
            onDeleteRule={handleDeletePricingRule}
            onToggleRule={handleTogglePricingRule}
            onUpdateRule={(rule) => updatePricingRuleMutation.mutate({ ruleId: rule.id, updates: rule })}
            onCloneRule={(rule) => {
              const { id, createdAt, updatedAt, ...ruleWithoutId } = rule;
              const clonedRule = { ...ruleWithoutId, name: `${rule.name} (Copy)`, isActive: false };
              setEditingPricingRule(clonedRule as any);
              setCreatePricingRuleOpen(true);
            }}
            onExecuteRule={handleExecutePricingRule}
            onExport={() => setExportPricingOpen(true)}
            onImport={() => setImportPricingOpen(true)}
          />
        );
      case 'availability':
        return availabilityLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3 p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 !border-[#0f1729]"></div>
            </div>
          </div>
        ) : (
          <AvailabilityRulesTable
            rules={availabilityRules}
            otaChannels={otaChannels}
            branchId={selectedBranchId}
            property={selectedProperty}
            basePrice={selectedProperty.basePrice}
            onCreateRule={() => { setEditingAvailabilityRule(null); setCreateAvailabilityRuleOpen(true); }}
            onEditRule={(rule) => { setEditingAvailabilityRule(rule); setCreateAvailabilityRuleOpen(true); }}
            onDeleteRule={handleDeleteAvailabilityRule}
            onToggleRule={handleToggleAvailabilityRule}
            onUpdateRule={(rule) => updateAvailabilityRuleMutation.mutate({ ruleId: rule.id, updates: rule })}
            onCloneRule={(rule) => {
              const { id, createdAt, updatedAt, ...ruleWithoutId } = rule;
              const clonedRule = { ...ruleWithoutId, name: `${rule.name} (Copy)`, isActive: false };
              setEditingAvailabilityRule(clonedRule as any);
              setCreateAvailabilityRuleOpen(true);
            }}
            onExecuteRule={handleExecuteAvailabilityRule}
            onExport={() => setExportAvailabilityOpen(true)}
            onImport={() => setImportAvailabilityOpen(true)}
          />
        );
      case 'events':
        return eventsLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3 p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 !border-[#0f1729]"></div>
            </div>
          </div>
        ) : (
          <EventsManager
            events={events}
            properties={properties}
            branches={branches}
            property={selectedProperty}
            currentPropertyId={selectedPropertyId}
            onCreateEvent={() => { setEditingEvent(null); setCreateEventOpen(true); }}
            onEditEvent={(event) => { setEditingEvent(event); setCreateEventOpen(true); }}
            onDeleteEvent={handleDeleteEvent}
            onExport={() => setExportEventsOpen(true)}
            onImport={() => setImportEventsOpen(true)}
          />
        );
      case 'calendar':
        return (
          <CalendarView
            property={selectedProperty}
            propertyId={selectedPropertyId}
            basePrice={selectedProperty?.basePrice || 0}
            pricingRules={pricingRules}
            availabilityRules={availabilityRules}
            events={events}
          />
        );
      case 'analytics':
        return <AnalyticsView propertyId={selectedPropertyId} />;
      case 'competitors':
        return (
          <CompetitorsView
            property={selectedProperty}
            propertyId={selectedPropertyId}
            propertyName={selectedProperty?.name || ''}
            basePrice={selectedProperty?.basePrice || 0}
            otaChannels={[
              { id: 'booking', name: 'Booking.com', code: 'booking', commission: 0 },
              { id: 'makemytrip', name: 'Make My Trip', code: 'makemytrip', commission: 0 }]
            }
            branches={branches}
          />
        );
      default:
        return (
          <div className="min-h-screen bg-background flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-muted-foreground">No Branch Present</h2>
              <p className="mt-2 text-sm text-muted-foreground">Please add a branch to get started.</p>
            </div>
          </div>
        );
    }
  };

  if (propertiesLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 !border-[#0f1729]"></div>
        </div>
      </div>
    );
  }

  if (branches.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-muted-foreground">No Branch Present</h2>
          <p className="mt-2 text-sm text-muted-foreground">Please add a branch to get started.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background revenue-manager-container">
      <div className="mx-auto max-w-full px-4 py-3 sm:px-3 lg:px-3">
        <Header properties={properties} selectedPropertyId={selectedPropertyId} onPropertyChange={setSelectedPropertyId} onAddProperty={() => setAddPropertyOpen(true)} />
        <div className="mt-3 space-y-6">
          {selectedProperty && (
            <>
              <StatsCards property={selectedProperty} rules={pricingRules} availabilityRules={availabilityRules} upcomingEventsCount={upcomingEventsCount} />
              <MetricsRow property={selectedProperty} rules={pricingRules} onBasePriceChange={handleBasePriceChange} />
            </>
          )}
          <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
          <div className="animate-fade-in">{renderTabContent()}</div>
        </div>
      </div>

      <AddPropertyDialog open={addPropertyOpen} onOpenChange={setAddPropertyOpen} onAdd={handleAddProperty} />
      <CreatePricingRuleDialog
        open={createPricingRuleOpen}
        onOpenChange={(open) => { setCreatePricingRuleOpen(open); if (!open) setEditingPricingRule(null); }}
        onSave={handleSavePricingRule}
        otaChannels={otaChannels}
        basePrice={selectedProperty?.basePrice || 0}
        editingRule={editingPricingRule}
        branchId={selectedBranchId}
        orgId={selectedOrgId}
        propertyId={selectedPropertyId}
        api_key={selectedProperty.apiId || ''}
        isLoading={createPricingRuleMutation.isPending || updatePricingRuleMutation.isPending}
      />
      <CreateAvailabilityRuleDialog
        open={createAvailabilityRuleOpen}
        onOpenChange={(open) => { setCreateAvailabilityRuleOpen(open); if (!open) setEditingAvailabilityRule(null); }}
        onSave={handleSaveAvailabilityRule}
        otaChannels={otaChannels}
        editingRule={editingAvailabilityRule}
        branchId={selectedBranchId}
        orgId={selectedOrgId}
        propertyId={selectedPropertyId}
        api_key={selectedProperty.apiId || ''}
        isLoading={createAvailabilityRuleMutation.isPending || updateAvailabilityRuleMutation.isPending}
      />
      <CreateEventDialog
        open={createEventOpen}
        onOpenChange={(open) => { setCreateEventOpen(open); if (!open) setEditingEvent(null); }}
        onSave={handleSaveEvent}
        properties={properties}
        branches={branches}
        editingEvent={editingEvent}
        orgId={String(selectedOrgId || '')}
        api_key={selectedProperty.apiId || ''}
        isLoading={createEventMutation.isPending || updateEventMutation.isPending}
      />

      {/* Export/Import Dialogs */}
      {selectedProperty && (
        <>
          <ExportRulesDialog
            open={exportPricingOpen}
            onOpenChange={setExportPricingOpen}
            rules={pricingRules}
            ruleType="pricing"
            property={selectedProperty}
            orgId={selectedOrgId}
          />
          <ImportRulesDialog
            open={importPricingOpen}
            onOpenChange={setImportPricingOpen}
            ruleType="pricing"
            properties={properties}
            currentPropertyId={selectedPropertyId}
            onImport={handleImportPricingRules}
          />
          <ExportRulesDialog
            open={exportAvailabilityOpen}
            onOpenChange={setExportAvailabilityOpen}
            rules={availabilityRules}
            ruleType="availability"
            property={selectedProperty}
            orgId={selectedOrgId}
          />
          <ImportRulesDialog
            open={importAvailabilityOpen}
            onOpenChange={setImportAvailabilityOpen}
            ruleType="availability"
            properties={properties}
            currentPropertyId={selectedPropertyId}
            onImport={handleImportAvailabilityRules}
          />
          <ExportRulesDialog
            open={exportEventsOpen}
            onOpenChange={setExportEventsOpen}
            rules={events.filter(event => event.propertyIds.includes(String(selectedBranchId)))}
            ruleType="events"
            property={selectedProperty}
            orgId={selectedOrgId}
          />
          <ImportRulesDialog
            open={importEventsOpen}
            onOpenChange={setImportEventsOpen}
            ruleType="events"
            properties={properties}
            currentPropertyId={selectedPropertyId}
            onImport={handleImportEvents}
          />
        </>
      )}
    </div>
  );
};

export default Index;